import React, { ReactNode, useEffect, useState, useContext } from 'react';
import classnames from 'classnames';
import styles from './multiFilterDropdown.module.scss';
import { BsChevronDown } from 'react-icons/bs';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import { MultiDropdownItem, ItemList } from 'v2/models';
import useOutsideClick from 'v2/hooks/useOutsideClick';
import Menu from './ComplementaryComponents/Menu';
import Blanket from './ComplementaryComponents/Blanket';
import MultiValueComponent from './ComplementaryComponents/MultiValueComponent';
import DropdownIndicatorComponent from './ComplementaryComponents/DropdownIndicatorComponent';
import MultiValueRemoveComponent from './ComplementaryComponents/MultiValueRemoveComponent';
import ControlComponent from './ComplementaryComponents/ControlComponent';
import ControlComponentAlternative from './ComplementaryComponents/ControlComponentAlternative';
import OptionComponent from './ComplementaryComponents/OptionComponent';
import ControlMultiComponent from './ComplementaryComponents/ControlMultiComponent';
import { getMultiSelectStyles, getSelectStyles } from './Styles/SelectStyles';
import {
  InputActionMeta,
  MultiValue,
  GroupBase,
  OptionsOrGroups,
  SelectComponentsConfig,
} from 'react-select';
import { CurrentStateFilterContext } from 'v2/context/currentStateFilterContext';

export interface SelectOption<T> {
  readonly value: T;
  readonly label: string;
}

interface CountMap {
  [id: string]: number;
}

export type MultiFilterGroup<T> = GroupBase<SelectOption<T>>;
export type MultiFilterOption<T> = SelectOption<T>;
export type MultiFilterOptions<T> = OptionsOrGroups<
  SelectOption<T>,
  GroupBase<SelectOption<T>>
>;
export interface FilterDropdownProps<T, TOptions = MultiFilterOptions<T>> {
  defaultOptions: TOptions;
  id: string;
  placeHolder?: string;
  secondaryPlaceHolder?: string;
  noOptionsMessage?: string;
  disabled?: boolean;
  loadOptions: (
    inputValue: string,
    callback: (options: MultiFilterOptions<T>) => void
  ) => void;
  onChange?: (option: ItemList) => void;
  alternate?: boolean;
  formatGroupLabel?: (group: GroupBase<SelectOption<T>>) => React.ReactNode;
  components?: Partial<
    SelectComponentsConfig<SelectOption<T>, true, GroupBase<SelectOption<T>>>
  >;
  className?: string;
}

const Dropdown = ({
  children,
  id,
  isOpen,
  setIsOpen,
  target,
  onClose,
  className,
}: {
  children?: ReactNode;
  id: string;
  readonly isOpen: boolean;
  readonly setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readonly target: ReactNode;
  readonly onClose: () => void;
  className?: string;
}) => {
  const handleClickOutside = () => {
    setIsOpen(false);
  };

  const ref = useOutsideClick(handleClickOutside, id);

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      className={classnames(id, className)}
      data-testid={`${id}-main`}
    >
      <div style={{ position: 'relative' }} ref={ref}>
        {target}
        {isOpen ? <Menu>{children}</Menu> : null}
        {isOpen ? <Blanket onClick={onClose} /> : null}
      </div>
    </div>
  );
};

function FilterDropdown<T extends MultiDropdownItem>({
  defaultOptions,
  loadOptions,
  id,
  onChange,
  placeHolder = 'Select an Option',
  noOptionsMessage = 'No options',
  secondaryPlaceHolder = 'Select items',
  disabled = false,
  alternate = false,
  formatGroupLabel,
  components = {},
  className,
}: FilterDropdownProps<T>) {
  type TOption = MultiFilterOption<T>;
  type TGroup = MultiFilterGroup<T>;
  type TOptions = MultiFilterOptions<T>;

  const [isOpen, setIsOpen] = useState(false);
  const [fetchedOptions, setFetchedOptions] =
    useState<TOptions>(defaultOptions);
  const [input, setInput] = useState('');
  const { currentState, setCurrentState } = useContext(
    CurrentStateFilterContext
  );

  useEffect(() => {
    onChange?.(currentState);
    if (alternate) {
      setInput('');
    }
  }, [currentState]);

  useEffect(() => {
    setFetchedOptions(defaultOptions);
  }, [defaultOptions]);

  useEffect(() => {
    if (isOpen === false) {
      setInput('');
      setFetchedOptions(defaultOptions);
    }
  }, [isOpen]);

  const handleMultiSelectChange = (newSelectedItems: MultiValue<TOption>) => {
    const idCounter: CountMap = {};
    newSelectedItems.forEach((item) => {
      idCounter[(item.value as MultiDropdownItem).id] =
        (idCounter[(item.value as MultiDropdownItem).id] || 0) + 1;
    });
    const newArray = newSelectedItems.filter((item) => {
      return idCounter[(item.value as MultiDropdownItem).id] === 1;
    });
    const newElements = newArray.map(
      (element) => element.value
    ) as unknown as MultiDropdownItem[];
    setCurrentState((previous) => {
      return {
        ...previous,
        list: newElements,
      };
    });
  };

  const dropdownSelectValue = currentState.list.map((e) => {
    return {
      label: e.name,
      value: e as unknown as T,
    };
  });

  const handleLoadOptions = (
    inputValue: string,
    callback: (options: TOptions) => void
  ) => {
    loadOptions(inputValue, (options) => {
      setFetchedOptions(options);
      callback(options);
    });
  };

  const handleInputChange = (value: string, action: InputActionMeta) => {
    if (action.action === 'input-change') {
      setInput(value);
      if (!value) {
        setFetchedOptions(defaultOptions);
      }
    }
  };

  return (
    /* eslint-disable react/display-name */
    <Dropdown
      id={id}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onClose={() => null}
      className={classnames(
        styles.dropDown,
        {
          [styles.disabled]: disabled,
        },
        className
      )}
      target={
        <>
          {currentState.is_all && !alternate ? (
            <div
              onClick={() =>
                setIsOpen((prev) => {
                  if (prev) {
                    setInput('');
                  }
                  return !prev;
                })
              }
              className={classnames(styles.inputContainer)}
              role="button"
              data-testid={`${id}-dataContainer`}
            >
              <span>{placeHolder}</span>
              <BsChevronDown
                style={{
                  strokeWidth: 1.5,
                  width: 13,
                  height: 13,
                  stroke: '#285FCB',
                }}
              />
            </div>
          ) : (
            <CreatableSelect<TOption, true, TGroup>
              onChange={handleMultiSelectChange}
              value={dropdownSelectValue}
              isMulti
              backspaceRemovesValue={false}
              hideSelectedOptions={true}
              isClearable={false}
              menuIsOpen={false}
              styles={getMultiSelectStyles()}
              placeholder={secondaryPlaceHolder}
              components={{
                IndicatorSeparator: null,
                Control: (props) => (
                  <ControlMultiComponent props={props} setIsOpen={setIsOpen} />
                ),
                MultiValue: MultiValueComponent,
                MultiValueRemove: MultiValueRemoveComponent,
              }}
            />
          )}
        </>
      }
    >
      <AsyncSelect<TOption, true, TGroup>
        autoFocus
        placeholder="Search"
        aria-label="filter-dropdown"
        components={{
          IndicatorSeparator: null,
          DropdownIndicator: DropdownIndicatorComponent,
          Control: alternate ? ControlComponentAlternative : ControlComponent,
          Option: OptionComponent,
          ...components,
        }}
        formatGroupLabel={formatGroupLabel}
        backspaceRemovesValue={false}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        tabSelectsValue={false}
        isClearable={false}
        menuIsOpen={!currentState.is_all}
        isDisabled={currentState.is_all}
        styles={getSelectStyles(currentState)}
        closeMenuOnSelect={false}
        isMulti={true}
        loadOptions={handleLoadOptions}
        value={dropdownSelectValue}
        defaultOptions={fetchedOptions}
        noOptionsMessage={() => noOptionsMessage}
        onChange={handleMultiSelectChange}
        inputValue={input}
        onInputChange={handleInputChange}
        id={id}
      />
    </Dropdown>
  );
}
export default FilterDropdown;
